import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import StorageService from "../services/StorageService";
import { getViewProfileRouteByRole } from "../utils/roleSwitcher";
import { getDashboardRoute } from "../utils/getDashboardRoute";
import PlatformNotificationBell from "./PlatformNotificationBell";

/** Label from stored user after login (API typically returns `name`). */
function getUserDisplayName(user) {
  if (!user || typeof user !== "object") return "";
  const raw =
    user.name ??
    user.user_name ??
    user.full_name ??
    user.contact_name ??
    user.company_name;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  const email = user.email;
  if (typeof email === "string" && email.includes("@")) {
    return email.split("@")[0].trim();
  }
  return "User";
}

function isSellerUser(user) {
  if (!user) return false;
  let userTypeId = user.user_type_id;
  if (userTypeId === null || userTypeId === undefined) {
    userTypeId = "";
  } else {
    userTypeId = String(userTypeId).trim();
  }
  const typeIds = userTypeId
    ? userTypeId.split(",").map((id) => String(id).trim()).filter((id) => id && id !== "null" && id !== "undefined")
    : [];
  const singleId = parseInt(userTypeId, 10);
  const isSellerById =
    (!Number.isNaN(singleId) && userTypeId.indexOf(",") === -1 && (singleId === 2 || singleId === 3)) ||
    typeIds.some((id) => {
      const numId = parseInt(id, 10);
      return id === "2" || id === "3" || numId === 2 || numId === 3;
    });
  return isSellerById;
}

/**
 * Dark top bar for public marketing pages (Home, Contract Manufacturers, RFQ).
 * Shows Login / Sign Up when logged out; Dashboard / My profile / Logout when logged in
 * so public flows match the authenticated app header behaviour.
 */
export default function WebsiteTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => StorageService.getUser());
  const [isAuthed, setIsAuthed] = useState(() => StorageService.isAuthenticated());

  const syncAuth = useCallback(() => {
    setUser(StorageService.getUser());
    setIsAuthed(StorageService.isAuthenticated());
  }, []);

  useEffect(() => {
    syncAuth();
  }, [location.pathname, syncAuth]);

  useEffect(() => {
    window.addEventListener("storage", syncAuth);
    window.addEventListener("focus", syncAuth);
    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener("focus", syncAuth);
    };
  }, [syncAuth]);

  const seller = isSellerUser(user);
  const displayName = getUserDisplayName(user);

  const handleLogout = () => {
    StorageService.clearAuth();
    setUser(null);
    setIsAuthed(false);
    navigate("/login");
  };

  return (
    <div className="home-top-bar">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <div className="home-top-logo">
            <Link to="/home" className="home-top-logo-link">
              <span className="home-top-logo-text">TROCANET</span>
              <span className="home-top-logo-subtitle">B2B Marketplace</span>
            </Link>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
            <Link to="/buyer-rfq-form" className="home-top-btn home-top-btn-rfq">
              Request for Quotation
            </Link>
            {seller && (
              <Link to="/auction/create" className="home-top-btn home-top-btn-rfq">
                🛒 Post Auction
              </Link>
            )}
            {isAuthed ? (
              <>
                <PlatformNotificationBell
                  variant="light"
                  buttonClassName="btn btn-link text-white p-1 position-relative border-0"
                />
                {displayName && (
                  <span className="home-top-user-name text-nowrap" title={user?.email || ""}>
                    Hi, {displayName}
                  </span>
                )}
                <Link to={getDashboardRoute()} className="home-top-btn home-top-btn-rfq">
                  Dashboard
                </Link>
                <Link to={getViewProfileRouteByRole()} className="home-top-btn home-top-btn-rfq">
                  My profile
                </Link>
                <button
                  type="button"
                  className="home-top-btn home-top-btn-login"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="home-top-btn home-top-btn-login">
                  Login
                </Link>
                <Link to="/signup" className="home-top-btn home-top-btn-signup">
                  Sign Up
                </Link>
              </>
            )}
            <select className="home-top-select" aria-label="Language">
              <option>Eng</option>
            </select>
            <select className="home-top-select" aria-label="Currency">
              <option>INR</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
